// The feed: newest first, only from people you follow. Nothing is ranked,
// promoted, or recommended; when something produced nothing to do, it says
// so. Outbound links are allowed here. Search reaches everyone's items
// behind an explicit scope toggle (spec section 5, "Search on a surface").
import { useEffect, useRef } from "react";
import type { Descriptor } from "../shell/descriptor.js";
import { Avatar } from "../shell/Avatar.js";
import { FeedHero, heroCandidates, verbFor } from "../shell/FeedHero.js";
import { EvChip, PagedList, useExpert } from "../shell/fragments.js";
import { artFor } from "../shell/placeholderArt.js";
import { ExpertSelect, scopeTabs, SearchBox, useDebouncedSearch } from "../shell/SurfaceSearch.js";
import { useStore, type AppStore } from "../state/store.js";
import type { FeedItemView } from "../state/types.js";
import { artImg } from "../assets.js";

function LoadOnce() {
  const feed = useStore((s) => s.feed);
  const loadFeed = useStore((s) => s.loadFeed);
  useEffect(() => {
    if (!feed) void loadFeed();
  }, []);
  return null;
}

function FeedItem({ it, focused, saved, read }: { it: FeedItemView; focused: boolean; saved: boolean; read: boolean }) {
  const s = useStore();
  const e = useExpert(it.expertId);
  const ref = useRef<HTMLElement>(null);
  // Arriving from Worth a look no longer scrolls the list to the article
  // (31 Jul 2026, Rob): the hero opens on it at the top of the page instead,
  // and the row keeps its marker for when the reader scrolls down to it.
  const verb = verbFor(it.kind);
  const host = it.url ? it.url.replace(/^https?:\/\//, "").split("/")[0] : "";
  return (
    <article ref={ref} className={`fitem glass${focused ? " focused" : ""}`} style={{ scrollMarginTop: 16 }}>
      <button onClick={() => s.nav("expert", it.expertId)} aria-label={`Profile: ${e.name}`}>
        <Avatar e={e} size="m" />
      </button>
      <div>
        <div className="fh">
          {/* The name is a door like the face beside it (31 Jul 2026, Rob):
              both go to the person's page. */}
          <button className="fname" onClick={() => s.nav("expert", it.expertId)}>
            {e.short ?? e.name}
          </button>
          <span>
            {it.kind ?? "Source"}
            {it.date ? ` · ${it.date}` : ""}
          </span>
          {/* On a card too narrow for the words the actions shrink to
              glyphs (29 Jul 2026): wrapping the buttons under the name row
              read as part of the article. Both forms render; the card's
              container width picks one. */}
          <span style={{ marginLeft: "auto", display: "flex", alignItems: "center", gap: 4 }}>
            <button
              className="btn quiet"
              aria-label={saved ? "Saved" : "Save"}
              title={saved ? "Saved" : "Save"}
              onClick={() => void s.saveFeedItem(it.id, !saved)}
            >
              <span className="btxt">{saved ? "Saved ✓" : "Save"}</span>
              <span className="bico" aria-hidden="true">{saved ? "★" : "☆"}</span>
            </button>
            <button
              className={`btn quiet${read ? " on" : ""}`}
              aria-label={read ? "Read" : "Mark read"}
              title={read ? "Read" : "Mark read"}
              onClick={() => void s.markFeedRead(it.id, !read)}
            >
              <span className="btxt">{read ? "Read ✓" : "Mark read"}</span>
              {/* The check the text form already uses, not an abstract dot
                  (reported 29 Jul 2026); weight and colour carry the state. */}
              <span className="bico" aria-hidden="true">{read ? "✔" : "✓"}</span>
            </button>
          </span>
        </div>
        <div className="fbody">
          {/* No article art yet: the item's first topic picks the generic,
              so a sleep piece at least reads as sleep (29 Jul 2026). */}
          <img
            className="fthumb"
            alt=""
            loading="lazy"
            decoding="async"
            sizes="(max-width: 620px) 100vw, 168px"
            {...artImg(artFor({ image: it.image, topicId: it.topics[0] ?? null }))}
          />
          <div className="ftext">
            <h3>{it.title}</h3>
            {it.summary && <p className="sum">{it.summary}</p>}
            {it.url && (
              <div className="fl">
                <a className="ext" href={it.url} target="_blank" rel="noopener noreferrer">
                  {verb} {host} ↗
                </a>
              </div>
            )}
          </div>
        </div>
        {/* An article with nothing to do just ends; the empty label was
            cut (29 Jul 2026). */}
        {it.drawn.length > 0 && (
          <div className="drawnrow">
            <span className="u">
              {it.drawn.length} practice{it.drawn.length > 1 ? "s" : ""} created from this
            </span>
            {/* Full-width rows, not chips: do-lines are whole sentences. */}
            {it.drawn.map((c) => (
              <button key={c.id} className="lrow drawnprac" onClick={() => s.openSheet({ kind: "practice", cardId: c.id })}>
                {c.chip && <EvChip chip={c.chip} />}
                <span className="grow">
                  <strong style={{ fontSize: 14 }}>{c.do}</strong>
                </span>
                <span className="chev">›</span>
              </button>
            ))}
          </div>
        )}
      </div>
    </article>
  );
}

// Debounce lives in a mounted component so the screen stays a pure function.
function FeedSearchRunner() {
  const q = useStore((s) => s.feedQ);
  useDebouncedSearch(q, () => void useStore.getState().runFeedSearch());
  return null;
}

export function feed(state: AppStore, focusId: string | null): Descriptor {
  const data = state.feed;
  const savedSet = new Set(data?.saved ?? []);
  const readSet = new Set(data?.read ?? []);
  const roster = state.experts ?? [];
  const followSet = new Set(state.appState?.follows ?? []);
  const chosen = state.feedExpert ? roster.find((e) => e.id === state.feedExpert) : undefined;

  // The toolbar row: search plus the source select, above the filter bar,
  // same shape on every surface (29 Jul 2026).
  const searching = state.feedQ.trim() !== "";
  const bar = (
    <div className="searchbar">
      <FeedSearchRunner />
      <SearchBox
        value={state.feedQ}
        onChange={(q) => state.setFeedQ(q)}
        placeholder="Search the feed…"
        label="Search the feed"
      />
      {/* While searching the source becomes the strip's third pane (31 Jul
          2026, Rob), so it leaves the toolbar rather than appearing twice. */}
      {!searching && (
        <ExpertSelect
          value={state.feedExpert}
          onChange={(id) => state.setFeedExpert(id)}
          kind="feed"
        />
      )}
    </div>
  );
  const notFollowingNotice = chosen && !followSet.has(chosen.id) && (
    <div className="notice info">
      <span className="ni">◎</span>
      <span>
        You’re not following {chosen.short ?? chosen.name}. Their feed is open to look at
        either way; following adds it to yours.
      </span>
    </div>
  );

  // A live query takes over the list: results render as ordinary feed items
  // so Save, Mark read, and the drawn practices all work in place. The
  // source select still applies; scope steps aside once a source is named.
  if (searching) {
    const results = state.feedSearch;
    return {
      nav: "feed",
      title: "Feed",
      bar,
      tabs: scopeTabs(
        state.feedScope,
        (sc) => {
          state.setFeedExpert(null);
          state.setFeedScope(sc);
        },
        { value: state.feedExpert, onChange: (id) => state.setFeedExpert(id), kind: "feed" },
      ),
      main: (
        <>
          <LoadOnce />
          {notFollowingNotice}
          {results === null ? (
            <section className="panel glass">
              <p className="empty">Searching…</p>
            </section>
          ) : results.length ? (
            <PagedList className="feedgrid" first={20} batch={10} key={`search:${state.feedScope}:${state.feedExpert ?? ""}`}>
              {results.map((it) => (
                <FeedItem
                  key={it.id}
                  it={it}
                  focused={false}
                  saved={savedSet.has(it.id)}
                  read={readSet.has(it.id)}
                />
              ))}
            </PagedList>
          ) : (
            <section className="panel glass">
              <p className="empty">
                {chosen
                  ? `Nothing from ${chosen.short ?? chosen.name} matches.`
                  : state.feedScope === "follows"
                    ? (
                        <>
                          Nothing from the people you follow matches.{" "}
                          <button
                            className="txt"
                            style={{ textDecoration: "underline" }}
                            onClick={() => state.setFeedScope("all")}
                          >
                            Search everyone.
                          </button>
                        </>
                      )
                    : "Nothing matches."}
              </p>
            </section>
          )}
        </>
      ),
    };
  }

  // New is the default lane: marking an article read moves it out (and out
  // of Has Practice, which is a lens on the unread feed); Saved and Read
  // are their own lanes and don't mind each other. The lanes are the
  // filter bar (29 Jul 2026, following Discover's), not chips.
  const all = data?.items ?? [];
  const laneOf = (it: FeedItemView, lane: string) => {
    if (lane === "action") return it.drawn.length > 0 && !readSet.has(it.id);
    if (lane === "saved") return savedSet.has(it.id);
    if (lane === "read") return readSet.has(it.id);
    return !readSet.has(it.id);
  };
  const items = all.filter((it) => laneOf(it, state.feedFilter));

  // The hero rides above the toolbar on every lane, so switching lanes does
  // not make the top of the screen jump; it stands down while searching,
  // where the answer is the list. Unread only: the picture is what has just
  // arrived, not a rerun of what you have already read.
  const heroItems = heroCandidates(all.filter((it) => !readSet.has(it.id)));

  return {
    nav: "feed",
    title: "Feed",
    hero: heroItems.length >= 2 ? <FeedHero items={heroItems} lead={focusId} /> : undefined,
    bar,
    tabs: {
      items: [
        ["new", "New"],
        ["saved", "Saved"],
        ["read", "Read"],
        ["action", "Has Practice"],
      ],
      counts: {
        new: all.filter((it) => laneOf(it, "new")).length,
        saved: all.filter((it) => laneOf(it, "saved")).length,
        read: all.filter((it) => laneOf(it, "read")).length,
        action: all.filter((it) => laneOf(it, "action")).length,
      },
      active: state.feedFilter,
      onPick: (t) => state.setFeedFilter(t),
    },
    main: (
      <>
        <LoadOnce />
        {notFollowingNotice}
        {/* Each item is its own card (28 Jul 2026): a shared panel with
            hairlines ran the articles together. */}
        {data === null ? (
          <section className="panel glass">
            <p className="empty">Loading…</p>
          </section>
        ) : items.length ? (
          <PagedList
            className="feedgrid"
            first={20}
            batch={10}
            key={`${state.feedFilter}:${state.feedExpert ?? ""}`}
          >
            {items.map((it) => (
              <FeedItem
                key={it.id}
                it={it}
                focused={it.id === focusId}
                saved={savedSet.has(it.id)}
                read={readSet.has(it.id)}
              />
            ))}
          </PagedList>
        ) : (
          <section className="panel glass">
            <p className="empty">
              {state.feedFilter === "saved"
                ? "Nothing saved yet. The Save button on any item keeps it here."
                : state.feedFilter === "read"
                  ? "Nothing marked read yet."
                  : chosen
                    ? `Nothing from ${chosen.short ?? chosen.name} yet.`
                    : (data?.items.length ?? 0) > 0
                      ? "Nothing new. Everything from the people you follow is read."
                      : "Nothing here yet. New items arrive as the people you follow publish."}
            </p>
          </section>
        )}
      </>
    ),
  };
}
